import { TablePaginationConfig } from "antd";
import { fleetsApi } from "api/fleet";
import { useDataLoadingContext } from "contexts/DataLoadingContext";
import { useNotificationContext } from "contexts/NotificationContext";
import { ITableData, defaultTableData } from "models";
import { IFleet } from "models/fleet";
import { ChangeEvent, useEffect, useRef, useState } from "react";

export default function useFleet() {
  const { showError } = useNotificationContext();
  const { reloadFleet } = useDataLoadingContext();
  const [data, setData] = useState<ITableData<IFleet> & { category: string }>({ ...defaultTableData, category: "" });
  const isFetchingRef = useRef(false);
  const lastFetchParamsRef = useRef<{ page: number; search: string; category: string; reloadFleet: number } | null>(null);

  useEffect(() => {
    // Prevent duplicate fetches with the same parameters
    const currentParams = {
      page: data.page,
      search: data.search,
      category: data.category,
      reloadFleet: reloadFleet,
    };

    // Check if we're already fetching or if parameters haven't changed
    if (
      isFetchingRef.current ||
      (lastFetchParamsRef.current &&
        lastFetchParamsRef.current.page === currentParams.page &&
        lastFetchParamsRef.current.search === currentParams.search &&
        lastFetchParamsRef.current.category === currentParams.category &&
        lastFetchParamsRef.current.reloadFleet === currentParams.reloadFleet)
    ) {
      return;
    }

    // Mark as fetching and store params BEFORE starting the fetch
    isFetchingRef.current = true;
    lastFetchParamsRef.current = currentParams;

    setData((prev) => ({ ...prev, loading: true }));

    fleetsApi(data.page, data.per_page, data.search, data.category)
      .then(({ data: response, error }) => {
        if (response) {
          // Deduplicate by ID to prevent showing the same fleet twice
          const seenIds = new Set<string>();
          const uniqueData = response.data
            .map((value) => ({
              ...value,
              key: value.id,
            }))
            .filter((value) => {
              if (seenIds.has(value.id)) {
                console.warn(`Duplicate fleet detected with ID: ${value.id}`);
                return false;
              }
              seenIds.add(value.id);
              return true;
            });

          setData((prev) => ({
            ...prev,
            ...response,
            data: uniqueData,
            loading: false,
          }));
        } else {
          showError(error!.message);
          setData((prev) => ({ ...prev, loading: false }));
        }
      })
      .catch(() => {
        showError("Failed to fetch fleets");
        setData((prev) => ({ ...prev, loading: false }));
      })
      .finally(() => {
        isFetchingRef.current = false;
      });
    // eslint-disable-next-line
  }, [data.page, data.search, data.category, reloadFleet]);

  const handlePageChange = ({ current }: TablePaginationConfig) => {
    if (current) setData((prev) => ({ ...prev, page: current }));
  };

  const handleSearch = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({ ...prev, search: value ?? "", page: 1 }));
  };

  const handleCategoryChange = (value: string) => {
    setData((prev) => ({ ...prev, category: value, page: 1 }));
  };

  return { data, handlePageChange, handleSearch, handleCategoryChange };
}
